import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useChartColorDefinitions } from '@/components/charts/constants';
import { TileVariant } from '@/components/diagrams/components/Tile';
import { Node } from '@/components/diagrams/FlowChart';
import { FormConfigurationProvider } from '@/components/form/context/FormConfigurationContext';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { HeaderCellTypography } from '@/components/tables/components/HeaderCellTypography';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { LegendColorRenderer } from '@/components/tables/DataTable/renderers/cell/LegendColorRenderer';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { TileNotesFieldWithGenerate } from '@/modules/summaryPanels/TileNotesSummaryPanel/TileNotesFieldWithGenerate';
import { AfterDeath } from '@/types/schema';

import { GraphVizNodeComponentType } from '../graphVizNodeConfig';
import { GraphVizGroupBasicDetailsForm } from './GraphVizGroupBasicDetailsForm';
import { VisualizationGroup } from './grouping';

interface GroupFormShape {
  displayName: string;
  description: string;
  nodeIds: string[];
  hideGroupContents: boolean;
  applyToAllSections: boolean;
  componentType: GraphVizNodeComponentType;
}

const memberColumns: GridColDef[] = [
  {
    field: 'sectionIndicator',
    headerName: '',
    width: 55,
    minWidth: 55,
    maxWidth: 55,
    sortable: false,
    align: 'center',
    renderCell: LegendColorRenderer({
      backgroundColor: ({ row }) => row.sectionIndicator.backgroundColor,
    }),
  },
  {
    field: 'entity',
    headerName: 'Items',
    flex: 1,
    renderCell: TwoLineTextRenderer({
      lineOneProps: { component: 'p', variant: 'title-xs' },
    }),
  },
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    width: 120,
    align: 'right',
    renderHeader: () => (
      <HeaderCellTypography>Include in group</HeaderCellTypography>
    ),
  },
];

export function VisualizationGroupModal({
  group,
  candidates,
  allowApplyToOtherSections,
  onSave,
  onDelete,
  onClose,
}: {
  group: VisualizationGroup;
  candidates: Node[];
  allowApplyToOtherSections: boolean;
  onSave: (group: VisualizationGroup, applyToOtherSections: boolean) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  // Named groups can be edited and ungrouped before the diagram is saved.
  const isCreating = !group.displayName;
  const colors = useChartColorDefinitions();
  const form = useForm<GroupFormShape>({
    defaultValues: {
      displayName: group.displayName,
      description: group.description ?? '',
      nodeIds: group.nodeIds,
      hideGroupContents: group.hideGroupContents ?? false,
      applyToAllSections: false,
      componentType:
        (candidates.find((node) => node.data.visualizationGroupId === group.id)
          ?.data.componentType as GraphVizNodeComponentType) ??
        group.componentType ??
        GraphVizNodeComponentType.Tile,
    },
  });
  const { formRef, handleSave } = useFormSaveHandler();
  const nodeIds = useWatch({ control: form.control, name: 'nodeIds' });
  const displayName = useWatch({ control: form.control, name: 'displayName' });
  const rows = useMemo(
    () =>
      candidates
        .filter((node) => node.type === 'tile' && !node.data.isGroupNode)
        .map((node) => {
          let backgroundColor = colors.SECONDARY.backgroundColor;
          if (node.data.variant === TileVariant.Primary)
            backgroundColor = colors.PRIMARY.backgroundColor;
          if (node.data.variant === TileVariant.Tertiary)
            backgroundColor = colors.TERTIARY.backgroundColor;
          return {
            id: node.id.includes(':')
              ? node.id.split(':').slice(1).join(':')
              : node.id,
            entity: {
              lineOne:
                typeof node.data.lineOne === 'string'
                  ? node.data.lineOne
                  : node.id,
              lineTwo:
                typeof node.data.lineTwo === 'string' ? node.data.lineTwo : '',
            },
            sectionIndicator: { backgroundColor },
          };
        })
        .sort(
          (a, b) =>
            Number(group.nodeIds.includes(b.id)) -
            Number(group.nodeIds.includes(a.id))
        ),
    [candidates, colors, group.nodeIds]
  );
  const onSubmit = form.handleSubmit((values) => {
    if (!values.displayName.trim() || (isCreating && values.nodeIds.length < 2))
      return;
    onSave(
      {
        ...group,
        displayName: values.displayName.trim(),
        description: values.description,
        nodeIds: values.nodeIds,
        hideGroupContents: values.hideGroupContents,
        componentType: values.componentType,
      },
      values.applyToAllSections
    );
  });

  return (
    <FormProvider {...form}>
      <FormConfigurationProvider
        value={{
          optionalDisplayType: isCreating
            ? 'required-asterisk'
            : 'optional-label',
        }}
      >
        <FormModal
          isOpen
          onClose={onClose}
          heading={group.displayName ? 'Group settings' : 'Create new group'}
          rightHeaderContent={null}
          actions={
            <FormModalActions.Provider formState={form.formState}>
              {!isCreating && (
                <FormModalActions.DestructiveButton onClick={onDelete}>
                  Ungroup all
                </FormModalActions.DestructiveButton>
              )}
              <FormModalActions.CancelButton onClick={onClose} />
              <FormModalActions.SaveButton
                disabled={
                  !displayName.trim() || (isCreating && nodeIds.length < 2)
                }
                onClick={handleSave}
              >
                {isCreating ? 'Create group' : 'Save changes'}
              </FormModalActions.SaveButton>
            </FormModalActions.Provider>
          }
        >
          <form ref={formRef} onSubmit={onSubmit} noValidate>
            <GraphVizGroupBasicDetailsForm<GroupFormShape>
              allowApplyToOtherSections={allowApplyToOtherSections}
              slots={{
                NotesField: (
                  <TileNotesFieldWithGenerate<GroupFormShape>
                    mode="group"
                    control={form.control}
                    fieldName="description"
                    nodeIds={nodeIds}
                    namespace={
                      (group.namespace as AfterDeath) || AfterDeath.None
                    }
                    groupDisplayName={displayName}
                  />
                ),
                SelectTable: (
                  <DataTable
                    rows={rows}
                    columns={memberColumns}
                    sx={{
                      '& .MuiDataGrid-cellCheckbox': { paddingRight: '12px' },
                    }}
                    checkboxSelection
                    disableVirtualization
                    disableRowSelectionOnClick
                    pagination={false}
                    slots={{ footer: () => <></> }}
                    rowSelectionModel={nodeIds}
                    onRowSelectionModelChange={(ids) =>
                      form.setValue('nodeIds', ids.map(String))
                    }
                  />
                ),
              }}
            />
          </form>
        </FormModal>
      </FormConfigurationProvider>
    </FormProvider>
  );
}
